import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { DEFAULT_LOCALE } from 'config'
import PropTypes from 'prop-types'
import rtlDetect from 'rtl-detect'

import queryClient from '@/services/queryClient'

import { supportedLanguages } from './langs'

const DEFAULT_LOCALE_FALLBACK = 'en'

export const LanguageContext = createContext()

export const useLanguage = () => {
  return useContext(LanguageContext)
}
const currentLocale = localStorage.getItem('current-lang')
const isRtlDefault = rtlDetect.isRtlLang(currentLocale)

const getLocale = (locale) => {
  return (
    supportedLanguages.find((lang) => lang.id === locale)?.id ??
    DEFAULT_LOCALE ??
    DEFAULT_LOCALE_FALLBACK
  )
}

export const LanguageProvider = ({ children }) => {
  const [locale, setLocale] = useState(getLocale(currentLocale))
  const [isRtl, setIsRtl] = useState(isRtlDefault)

  const changeLang = useCallback((locale) => {
    const checkedLocale = getLocale(locale)
    setLocale(checkedLocale)
    localStorage.setItem('current-lang', checkedLocale)
    document.documentElement.lang = checkedLocale
    document.lang = checkedLocale
    queryClient.resetQueries()
  }, [])

  useEffect(() => {
    const rtlStatus = rtlDetect.isRtlLang(locale)
    setIsRtl(rtlStatus)

    document.dir = rtlStatus ? 'rtl' : 'ltr'
  }, [locale])

  useEffect(() => {
    document.setLocale = changeLang
  }, [changeLang])

  useEffect(() => {
    document.documentElement.lang = locale
    document.lang = locale
  }, [])

  const value = useMemo(
    () => ({ locale, changeLang, isRtl }),
    [locale, isRtl, changeLang],
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

LanguageProvider.propTypes = {
  children: PropTypes.any,
  languageCode: PropTypes.string,
}
